import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';
import { z } from 'zod';
import i18n from '@/Config/app/i18n';
import { setToken } from '@/Config/auth/authStore';
import { trackAndNavigate } from '@/Config/libs/analytics';
import { PostHogEventEnum, PostHogFeatureFlagEnum } from '@/Domain/enum/PostHogEvent.enum';
import errorMapper, { Error, ErrorCodes } from '@/Domain/utils/errorMapper';

const LoginValidator = z.object({
  username: z.string().email(),
  password: z.string().min(1, i18n.t('validation:password_required')),
  recaptcha_response_v3: z.string(),
  recaptcha_response_v2: z.string().optional(),
});

export type LoginForm = z.infer<typeof LoginValidator>;

export const useLogin = () => {
  const { t } = useTranslation('validation');
  const { track } = useAnalytics();
  const postHog = usePostHog();
  const navigate = useNavigate();

  const [isPending, setIsPending] = useState(false);
  const [useV2, setUseV2] = useState(false);

  const [tooManyRequests, setTooManyRequests] = useState<boolean>(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
    setValue,
    register,
  } = useForm<LoginForm>({
    resolver: zodResolver(LoginValidator),
  });

  register('recaptcha_response_v3', { required: true });

  const mutation = useMutation<
    { email: string; user_id: number; jwt_token: string; default_scenario_id: string | null },
    Error<LoginForm>,
    LoginForm
  >({
    mutationKey: ['accounts/login'],
    throwOnError: false,
    onSuccess: (response) => {
      // Store JWT from login response in auth store
      if (response.jwt_token) {
        setToken(response.jwt_token);
      }

      // Identify user in PostHog so this event and future events are linked to
      // the user. Keyed on the stable numeric user_id to preserve continuity
      // with anonymous→identified history captured pre-SPA migration.
      postHog.identify(response.user_id.toString());

      // Get the A/B test variant for tracking
      const backgroundVariant = postHog.getFeatureFlag(PostHogFeatureFlagEnum.SignupBackgroundTest);

      // Capture in PostHog for experiment analysis
      postHog.capture(PostHogEventEnum.LoginCompleted, {
        background_variant: backgroundVariant,
      });

      const scenarioId = response.default_scenario_id;
      const navigateTo = scenarioId ? `/i/${scenarioId}/` : '/';

      trackAndNavigate(
        'login_form_success',
        {
          event_category: 'User login',
          event_label: 'login_form',
          background_variant: backgroundVariant ?? 'unknown',
        },
        () => {
          // Full document navigation: the entry shell (login route) is a
          // separate router tree from the operator-app routes, so we always
          // reload to remount the SPA root with the post-login state.
          window.location.href = navigateTo;
        },
      );
    },
    onError: (error) => {
      setIsPending(false);
      track('login_form_error', { event_category: 'User login', event_label: 'login_form' });
      // eslint-disable-next-line
      errorMapper(error.response?.data?.errors || {}, (field, message, code, options) => {
        if (field === 'recaptcha_response_v3') {
          setUseV2(true);
          return;
        }

        if (code && code === ErrorCodes.TOO_MANY_REQUESTS) {
          setTooManyRequests(true);
          return;
        }

        if (code === ErrorCodes.NOT_ACTIVATED) {
          const userEmail = options?.user_email;
          navigate(
            userEmail
              ? `/register/verify?user_email=${encodeURIComponent(userEmail)}`
              : '/register/verify',
            { viewTransition: true },
          );
          return;
        }

        setError('username', { message: t('invalid_credentials') });
        setError('password', { message: t('invalid_credentials') });
      });
    },
  });

  const onSubmit = (data: LoginForm) => {
    track('login_form_submit', { event_category: 'User login', event_label: 'login_form' });
    setIsPending(true);
    setTooManyRequests(false);
    return mutation.mutate(data);
  };

  const onVerify = useCallback(
    (token: string) => {
      setValue('recaptcha_response_v3', token);
    },
    [mutation.submittedAt],
  );

  return {
    control,
    useV2,
    register,
    errors,
    handleSubmit,
    onSubmit,
    onVerify,
    isPending,
    tooManyRequests,
  };
};
